import { Box, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import type { FC } from 'react';

import { SectionSubtitle, SectionTitle } from '../../../components/analyticsUi';

import type { AssessmentCompletionBreakdown } from 'hooks/analytics';
import useTranslate from 'hooks/useTranslate';
import assessmentCompletionColors from '../assessmentCompletionColors';

interface CompletionModeBreakdownProps {
    breakdown: AssessmentCompletionBreakdown;
}

const CompletionModeBreakdown: FC<CompletionModeBreakdownProps> = ({ breakdown }) => {
    const { translate } = useTranslate();
    const rows = [
        {
            key: 'self',
            label: translate('assessmentModeSelf'),
            color: assessmentCompletionColors.selfAssessment,
            value: breakdown.self,
        },
        {
            key: 'agentOnly',
            label: translate('assessmentModeAgentOnly'),
            color: assessmentCompletionColors.agentAssisted,
            value: breakdown.agentOnly,
        },
        {
            key: 'mixed',
            label: translate('assessmentModeMixed'),
            color: assessmentCompletionColors.mixed,
            value: breakdown.mixed,
        },
    ] as const;

    return (
        <Paper component="section" variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 2 }}>
            <SectionTitle>{translate('assessmentCompletionModeBreakdown')}</SectionTitle>
            <SectionSubtitle>{translate('assessmentAgentAssistedExplanation')}</SectionSubtitle>
            <Stack spacing={1.75}>
                {rows.map(({ key, label, color, value }) => (
                    <Box key={key}>
                        <Stack
                            direction="row"
                            sx={{
                                alignItems: 'baseline',
                                justifyContent: 'space-between',
                                mb: 0.5,
                            }}
                        >
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{label}</Typography>
                            <Typography
                                color="text.secondary"
                                sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
                            >
                                {value.count} · {value.percentage.toFixed(1)}%
                            </Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min(100, Math.max(0, value.percentage))}
                            aria-label={label}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'action.hover',
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: color,
                                    borderRadius: 4,
                                },
                            }}
                        />
                    </Box>
                ))}
            </Stack>
        </Paper>
    );
};

export default CompletionModeBreakdown;
